import { toInteger } from 'lodash';
import type { MarkItemFormValue } from 'feature-components/mark-response/mark-response-form/mark-response-form.schema';
import {
  type MarkResponseFormValues,
  SubmitAction,
  markHasIncompleteScores
} from 'feature-components/mark-response/mark-response-form/mark-response-form.schema';
import type {
  PatchTestInstanceResponseMarkItem,
  PostAndPatchMarkItemFields,
  PostTestInstanceResponseMarkItem
} from 'services/assist-api/entities/test-instance-response-mark-item/types';
import type {
  PostAndPatchMarkFields,
  TestInstanceResponseMarkWithItems
} from 'services/assist-api/entities/test-instance-response-mark/types';
import {
  MarkReplacementReason,
  MarkStatus
} from 'services/assist-api/entities/test-instance-response-mark/types';
import { TestInstanceResponseInputType } from 'services/assist-api/entities/test-instance-response/types';

export function determineNewStatus(
  formValues: MarkResponseFormValues,
  defaultValues: MarkResponseFormValues
): MarkStatus {
  const canSubmitWithIncompleteMarks =
    markHasIncompleteScores(formValues.markItems) &&
    formValues.status === MarkStatus.FinishedFailure;

  // Allow the mark status to be overridden by a marking admin when manually selected:
  if (formValues.status !== defaultValues.status) {
    return formValues.status;
  }

  // Ensure that the mark is reviewed when submitting if it is flagged for attention.
  // When submitted with incomplete marks, the status should be set to Finished Failure
  // otherwise it can be considered successfully marked:
  if (formValues.submitAction === SubmitAction.SubmitMarking) {
    if (formValues.flagForAttention) {
      return MarkStatus.NeedsReview;
    }

    if (canSubmitWithIncompleteMarks) {
      return MarkStatus.FinishedFailure;
    }

    return MarkStatus.FinishedSuccess;
  }

  // If it is a draft, and the status is an editing status, change the status to In Progress so that
  // we have a record that the marker has started working on it (either for the first time, or again
  // after it has been through review):
  if (
    [MarkStatus.Assigned, MarkStatus.ReturnedForMarking, MarkStatus.Reassigned].includes(
      defaultValues.status
    )
  ) {
    return MarkStatus.InProgress;
  }
  // By default, when saving a draft, don't automatically change the status to avoid unexpected
  // status changes when the mark is being reviewed or is finished (the form should be disabled
  // for admins when the mark is finished; this is extra protection):
  return defaultValues.status;
}

/**
 * Determines the mark fields to save that are shared between POST and PATCH requests, with the
 * exception of its mark items.
 */
export function markFieldsToSave(
  responseId: number,
  rubricId: number,
  formValues: MarkResponseFormValues,
  defaultValues: MarkResponseFormValues,
  oldMarkId?: number
): PostAndPatchMarkFields {
  return {
    status: determineNewStatus(formValues, defaultValues),
    needsAttention: formValues.flagForAttention || false,
    comment: formValues.comment || '',
    activeMarkerId: toInteger(formValues.activeMarkerId), // There are checks in place to ensure that an active marker ID is set by the time that a mark is saved.
    testInstanceResponseId: responseId,
    testRubricId: rubricId,
    tagIds: formValues.attentionReasons.filter((ar) => ar.checked).map((ar) => ar.id),
    replacedTestInstanceResponseMarkId: oldMarkId,
    replacedTestInstanceResponseMarkReplacementReason: oldMarkId
      ? MarkReplacementReason.AutoscoringModified
      : undefined
  };
}

/**
 * Determines the mark item fields to save that are shared between POST and PATCH requests.
 */
export function markItemFieldsToSave(
  markItem: MarkItemFormValue,
  activeMarkerId: number,
  markId?: number
): PostAndPatchMarkItemFields {
  return {
    submittedValue: markItem.submittedValue || '',
    testInstanceResponseMarkId: markId,
    inputType: markItem.inputType,

    // The mark item marker ID is set in the Rubric Item onChange event so should not be undefined
    // at this point under normal circumstances. However there is an edge case where the new marker
    // is only allocated on save. For this scenario, the Rubric Item onChange would set the active
    // marker to undefined since there is no current marker yet to associate the mark item to.
    // Therefore, we can assume that if the mark item active marker ID is undefined and there is
    // a score that is manually scored, the testInstanceResponseMarkerId should be changed here to
    // the value of the now allocated current/active marker (these will be same at the this point in
    // the form's lifecycle).
    testInstanceResponseMarkerId:
      !markItem.marker?.id &&
      markItem.submittedValue &&
      markItem.inputType === TestInstanceResponseInputType.Manual
        ? activeMarkerId
        : toInteger(markItem.marker?.id),

    testInstanceResponseItemId: markItem.responseItemId,
    testRubricItemId: markItem.rubricItemId,
    submittedOptions: markItem.rubricItemOptionId ? [markItem.rubricItemOptionId] : []
  };
}

/**
 * Determines the mark items to update via individual PATCH requests. This function is only used
 * when updating a mark.
 */
export function markItemsToPatch(
  markItems: MarkItemFormValue[],
  activeMarkerId: number,
  existingMark: TestInstanceResponseMarkWithItems
): PatchTestInstanceResponseMarkItem[] {
  const changedMarkItems = markItems.filter((markItem: MarkItemFormValue): boolean => {
    const existingMarkItem = existingMark.markItems?.find((mi) => mi.id === markItem.id);
    return existingMarkItem
      ? existingMarkItem.submittedValue !== markItem.submittedValue &&
          markItem.submittedValue !== ''
      : false;
  });
  return changedMarkItems.map((markItem) => ({
    id: markItem.id || 0,
    data: markItemFieldsToSave(markItem, activeMarkerId, existingMark.id)
  }));
}

/**
 * Determines the mark items to include via a POST request (either when creating the mark as part of
 * its POST request or for individual POST requests when updating a mark). We should only create
 * scored mark items.
 */
export function markItemsToPost(
  markItems: MarkItemFormValue[],
  activeMarkerId: number,
  existingMarkId?: number
): PostTestInstanceResponseMarkItem[] {
  return markItems
    .filter((markItem) => markItem.id === undefined && markItem.submittedValue !== '')
    .map((markItem) => markItemFieldsToSave(markItem, activeMarkerId, existingMarkId));
}

/**
 * Determines the IDs of the mark items to remove via individual DELETE requests. This function is
 * only used when updating a mark (mark items don't exist until the mark is created, so there is
 * nothing to delete when creating).
 */
export function markItemIdsToDelete(markItems: MarkItemFormValue[]): number[] {
  return markItems
    .filter((markItem) => markItem.id !== undefined && markItem.submittedValue === '')
    .map((markItem) => toInteger(markItem.id));
}
